/**
 * Soar campaign PDP controller — document-level, so it works across the split
 * sections (hero, buy box, sticky CTA, subscribe CTA live in separate sections).
 *
 * Three independent modules, each activates only if its markup is present:
 *   - Subscription buy box   → reads [data-mpdp-config]
 *   - Presale/preorder box    → reads [data-mpdp-preorder-config]
 *   - Countdown timer         → reads [data-mpdp-countdown]
 * Generic bits (image gallery, scroll-to-checkout, open-cart) always run.
 */
(function () {
  function init() {
    if (window.__mandiPdpWired) return;
    window.__mandiPdpWired = true;

    var all = function (sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); };
    var num = function (v) { return parseFloat(String(v).replace(/[^0-9.]/g, '')) || 0; };
    var money = function (v) { return v.toFixed(2); };
    // Per-bar prices floor to 2 decimals (e.g. $35.99/10 = $3.599 shows $3.59, not $3.60) — the
    // small epsilon guards against float error flooring an exact price down a cent.
    var perbar = function (v) { return (Math.floor(v * 100 + 1e-6) / 100).toFixed(2); };

    // XHR (not fetch) to dodge fetch-wrapping analytics pixels that break cart AJAX.
    function xhr(method, url, data, cb) {
      var x = new XMLHttpRequest();
      x.open(method, url, true);
      if (data) { x.setRequestHeader('Content-Type', 'application/json'); x.setRequestHeader('X-Requested-With', 'XMLHttpRequest'); }
      x.onload = function () { try { cb(JSON.parse(x.responseText)); } catch (e) { cb({}); } };
      x.onerror = function () { cb({}); };
      x.send(data ? JSON.stringify(data) : null);
    }

    var toastTimer;
    function showToast(msg) {
      var el = document.querySelector('[data-mpdp-toast]');
      if (!el) return;
      var m = el.querySelector('[data-mpdp-toast-msg]'); if (m) m.textContent = msg;
      el.hidden = false;
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { el.hidden = true; }, 2200);
    }

    function refreshCount() {
      xhr('GET', '/cart.js', null, function (c) {
        if (c && typeof c.item_count === 'number') all('[data-mpdp-cart-count]').forEach(function (el) { el.textContent = c.item_count; });
      });
    }

    // Shared add-to-cart: builds the drawer refresh, publishes cart events.
    function cartAdd(item, msg) {
      var body = { items: [item] };
      var drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.sections = drawer.getSectionsToRender().map(function (s) { return s.id; }).join(',');
        body.sections_url = window.location.pathname;
      }
      xhr('POST', '/cart/add.js', body, function (res) {
        if (res && res.status && res.status !== 200) { showToast(res.description || 'Could not add'); return; }
        showToast(msg);
        refreshCount();
        if (drawer) {
          drawer.classList.remove('is-empty');
          try { if (typeof drawer.renderContents === 'function') drawer.renderContents(res); else if (drawer.open) drawer.open(); }
          catch (e) { if (drawer.open) drawer.open(); }
          if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
            try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'mandi-pdp' }); } catch (e) {}
          }
        }
      });
    }

    // Scroll to the first VISIBLE buy box (mobile vs desktop-combined swap by CSS), and
    // land on the actual CTAs — Add to Cart / one-time (fudge) or Reserve (PBCC) — so the
    // buttons are in the fold, not the top of a tall buy box. Sit the lowest CTA near the
    // viewport bottom so the pack options above it stay visible; never scroll above the box.
    function scrollToCheckout() {
      var els = document.querySelectorAll('[data-mpdp-checkout]');
      var el = null;
      for (var i = 0; i < els.length; i++) { if (els[i].offsetParent !== null) { el = els[i]; break; } }
      if (!el) return;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var boxTop = el.getBoundingClientRect().top + window.scrollY - 12;
      var ctas = el.querySelectorAll('[data-mpdp-add-sub], [data-mpdp-add-onetime], [data-mpdp-reserve]');
      var lowest = null;
      for (var j = 0; j < ctas.length; j++) {
        if (ctas[j].offsetParent === null) continue;
        if (!lowest || ctas[j].getBoundingClientRect().bottom > lowest.getBoundingClientRect().bottom) lowest = ctas[j];
      }
      if (lowest) {
        var r = lowest.getBoundingClientRect();
        if (r.top >= 0 && r.bottom <= vh) return; // CTAs already fully in view — don't jump
        var top = Math.max(boxTop, r.bottom + window.scrollY - vh + 24);
        window.scrollTo({ top: top, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: boxTop, behavior: 'smooth' });
      }
    }

    // ─────────── Subscription buy box ───────────
    function initSubscription(cfgEl) {
      var cfg;
      try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
      var FLAVORS = cfg.flavors || null;
      function packsFor() { return FLAVORS ? (((FLAVORS[state.flavor] || {}).packs) || {}) : (cfg.packs || {}); }
      function headlinesFor() { return FLAVORS ? (((FLAVORS[state.flavor] || {}).headlines) || {}) : (cfg.headlines || {}); }
      var startFlavor = FLAVORS ? ((cfg.defaultFlavor && FLAVORS[cfg.defaultFlavor]) ? cfg.defaultFlavor : Object.keys(FLAVORS)[0]) : null;
      // ?flavor=pbcc deep-links straight into a flavor (homepage flavor cards, ads, email).
      if (FLAVORS) {
        var urlFlavor = null;
        try { urlFlavor = new URLSearchParams(window.location.search).get('flavor'); } catch (e) {}
        if (urlFlavor && FLAVORS[urlFlavor]) startFlavor = urlFlavor;
      }
      // a flavor may opt out of subscriptions entirely (e.g. PBCC has no selling plan yet)
      function flavorCfg() { return FLAVORS ? (FLAVORS[state.flavor] || {}) : cfg; }
      function subEnabled() { return flavorCfg().subscribe !== false; }
      var startFC = FLAVORS ? (FLAVORS[startFlavor] || {}) : cfg;
      var sizesRef = startFC.packs || cfg.packs || {};
      var sizes = Object.keys(sizesRef);
      var preferredSize = startFC.defaultSize || cfg.defaultSize;
      var startSize = (preferredSize && sizesRef[preferredSize]) ? preferredSize : (sizes.indexOf('10') > -1 ? '10' : (sizes[0] || '10'));
      // Default to SUBSCRIBE & SAVE when the flavor offers it (else one-time). `choice` is
      // the variety pack's free-box flavor.
      var state = { size: startSize, plan: (startFC.subscribe === false ? 'onetime' : 'subscribe'), flavor: startFlavor, choice: 'fudge', freq: '' };

      function render() {
        var PACKS = packsFor();
        var HEADLINES = headlinesFor();
        var pack = PACKS[state.size] || PACKS[sizes[0]] || {};
        var canSub = subEnabled();
        if (!canSub) state.plan = 'onetime';
        var isSub = canSub && state.plan === 'subscribe';

        // Subscription frequency: the Stay selling plans in this pack's group. Reset the
        // chosen frequency when it isn't valid for the current pack (e.g. after a size switch).
        var subFreqs = pack.freqs || [];
        var freqIds = subFreqs.map(function (p) { return String(p.id); });
        if (subFreqs.length && freqIds.indexOf(String(state.freq)) === -1) {
          // Default to "every 4 weeks" (or a monthly plan) when present, else the resolved
          // plan, else the first option.
          var pref = null, monthly = null;
          subFreqs.forEach(function (p) {
            var lb = p.label || '';
            if (!pref && /\b4\s*weeks?\b/i.test(lb)) pref = p;
            if (!monthly && /month/i.test(lb)) monthly = p;
          });
          var def = pref || monthly;
          state.freq = def ? String(def.id) : ((freqIds.indexOf(String(pack.plan)) > -1) ? String(pack.plan) : String(subFreqs[0].id));
        }
        all('[data-mpdp-freq]').forEach(function (sel) {
          if (subFreqs.length > 1) {
            var want = freqIds.join(',');
            if (sel.getAttribute('data-built') !== want) {
              sel.innerHTML = subFreqs.map(function (p) { return '<option value="' + p.id + '">' + (p.label || 'Subscription') + '</option>'; }).join('');
              sel.setAttribute('data-built', want);
            }
            sel.value = String(state.freq);
            sel.style.display = '';
          } else {
            sel.style.display = 'none';
          }
        });
        all('[data-mpdp-freq-label]').forEach(function (el) {
          el.textContent = (subFreqs[0] && subFreqs[0].label) || 'Delivered monthly';
          el.style.display = subFreqs.length > 1 ? 'none' : '';
        });
        // Auto-applied Mandi discount. codeAmt = flat $ off (takes precedence); codePct = % off.
        // Both default to 0 elsewhere, so every other PDP is unaffected. applyCode() is the one
        // place the discount is applied, so every figure below stays consistent. A flat $-off
        // leaves the struck "full" price alone and only lowers what they pay; a % code (legacy)
        // discounts the strike too.
        // Per-flavor auto-discount (e.g. PBCC 25% off) overrides the section-level code.
        var _fc = flavorCfg();
        var codePct = num(_fc.codePct) || num(cfg.codePct);
        var codeAmt = num(_fc.codeAmt) || num(cfg.codeAmt);
        var codeMul = codePct > 0 ? (1 - codePct / 100) : 1;
        var hasCode = codeAmt > 0 || codePct > 0;
        var applyCode = function (p) { return codeAmt > 0 ? Math.max(0, p - codeAmt) : p * codeMul; };
        var main = isSub ? num(pack.subfirst) : num(pack.onetime);
        var payMain = applyCode(main);
        var total = money(payMain);

        all('[data-mpdp-total]').forEach(function (el) { el.textContent = total; });
        var ctaLabel = flavorCfg().cta || cfg.cta || 'Add to Cart';
        all('[data-mpdp-cta-label]').forEach(function (el) { el.textContent = ctaLabel; });
        all('[data-mpdp-offer-headline]').forEach(function (el) { el.textContent = HEADLINES[state.size] || ''; });
        all('[data-mpdp-offer-suffix]').forEach(function (el) { el.textContent = isSub ? 'for your first box.' : 'one-time.'; });
        all('[data-mpdp-sub-only]').forEach(function (el) { el.style.display = isSub ? 'flex' : 'none'; });

        var basePack = PACKS['10'] || PACKS[sizes[0]] || {};
        var baseUnitOT = num(basePack.onetime);
        var baseCount = num(basePack.count) || 10;
        // Size tiles show the pack price with a struck "list" (N × one box) and a "Save $X"
        // chip on the multi-box tile — per Jake, no more $/bar. The list references the one box
        // price of the CURRENT plan, so the volume discount reads as real savings.
        var priceKey = isSub ? 'subfirst' : 'onetime';
        var baseUnit = num(basePack[priceKey]) || baseUnitOT;
        all('[data-mpdp-size]').forEach(function (card) {
          var key = card.getAttribute('data-mpdp-size');
          var cp = PACKS[key]; if (!cp) return;
          card.classList.toggle('is-active', key === state.size);
          var cnt = num(cp.count) || (key === '30' ? 30 : 10);
          var mult = baseCount > 0 ? cnt / baseCount : 1;      // 1 box → 1×, 3 boxes → 3×
          var nowUnit = applyCode(num(cp[priceKey]));
          // List = the variant's compare-at (e.g. 30-pack $134.99); fall back to N × one box.
          var listUnit = num(cp.compare);
          if (listUnit <= nowUnit + 0.005) listUnit = baseUnit * mult;
          var save = listUnit - nowUnit;
          var m = card.querySelector('[data-mpdp-main]'); if (m) m.textContent = money(nowUnit);
          var nowEl = card.querySelector('[data-mpdp-tile-now]'); if (nowEl) nowEl.textContent = money(nowUnit);
          var wasEl = card.querySelector('[data-mpdp-tile-was]');
          if (wasEl) { wasEl.textContent = '$' + money(listUnit); wasEl.style.display = save > 0.005 ? '' : 'none'; }
          var saveEl = card.querySelector('[data-mpdp-tile-save]');
          if (saveEl) { saveEl.textContent = 'Save $' + money(save); saveEl.style.display = save > 0.005 ? '' : 'none'; }
          // legacy hooks kept as no-ops where a template still carries them
          var pb = card.querySelector('[data-mpdp-perbar]'); if (pb) pb.textContent = perbar(nowUnit / (cnt || 1));
          var nt = card.querySelector('[data-mpdp-note]'); if (nt) nt.textContent = isSub ? ('first box, then $' + money(num(cp.renew))) : 'one-time';
          var bg = card.querySelector('[data-mpdp-bogo-badge]'); if (bg) bg.style.display = (cnt >= 30) ? '' : 'none';
        });

        // Variety free-box chooser active state.
        all('[data-mpdp-choice]').forEach(function (b) { b.classList.toggle('is-active', b.getAttribute('data-mpdp-choice') === state.choice); });

        all('[data-mpdp-plan]').forEach(function (row) {
          var key = row.getAttribute('data-mpdp-plan');
          row.classList.toggle('is-active', key === state.plan);
          var pre = key === 'subscribe' ? num(pack.subfirst) : num(pack.onetime);
          var payRow = applyCode(pre);
          var pr = row.querySelector('[data-mpdp-plan-price]');
          if (pr) pr.textContent = money(payRow);
          // One-time list price = N × one box (Jake: 3× one box for the 30-pack, even with the
          // volume discount). Only the one-time row carries [data-mpdp-plan-was]; the subscribe
          // row shows its own strike via [data-mpdp-sub-strike].
          var wasEl = row.querySelector('[data-mpdp-plan-was]');
          if (wasEl) {
            var mult = baseCount > 0 ? ((num(pack.count) || baseCount) / baseCount) : 1;
            // List = the variant's compare-at (e.g. 30-pack $134.99); fall back to N × one box.
            var listRef = num(pack.compare);
            if (listRef <= payRow + 0.005) listRef = baseUnitOT * mult;
            wasEl.textContent = '$' + money(listRef);
            wasEl.style.display = (listRef > payRow + 0.005) ? '' : 'none';
          }
        });

        // Subscription-forward buy box (soar PDP hero) — all no-ops if the hooks are absent.
        // One-time price also carries the auto-applied code (it discounts the whole order,
        // subscribe OR one-time). codeMul is 1 where no code → other PDPs unchanged. Keeps
        // this in step with the one-time per-bar below, which already applied codeMul.
        // Strike = the FULL reference: the pack's compare-at price if higher than the one-time
        // (e.g. 30-pack $134.99 = three single boxes), else the one-time. Flat $-off keeps it
        // un-discounted; a % code discounts it.
        var strikeBase = num(pack.compare) > num(pack.onetime) ? num(pack.compare) : num(pack.onetime);
        // Strike is always the FULL list price (compare-at / one-box × N), never
        // discounted by the code — e.g. PBCC subscribe shows $44.99 / $134.99 struck.
        var strikeVal = strikeBase;
        all('[data-mpdp-onetime]').forEach(function (el) { el.textContent = money(strikeVal); });
        // Saving = struck full price minus what they actually pay.
        all('[data-mpdp-saving]').forEach(function (el) { el.textContent = money(Math.max(0, strikeVal - payMain)); });
        all('[data-mpdp-renew]').forEach(function (el) { el.textContent = money(num(pack.renew)); });
        all('[data-mpdp-perday]').forEach(function (el) { el.textContent = perbar(applyCode(num(pack.subfirst)) / (pack.count || 1)); });
        all('[data-mpdp-onetime-perbar]').forEach(function (el) { el.textContent = perbar(applyCode(num(pack.onetime)) / (pack.count || 1)); });
        // One-time box label + free-shipping pill vary by size. Free shipping (over $49)
        // only clears on the 30-pack one-time; the 10-pack is the "Starter Pack".
        var isTen = state.size === '10';
        all('[data-mpdp-onetime-label]').forEach(function (el) { el.textContent = isTen ? 'Or, Get the Starter Pack (10-Pack)' : 'Or, One-Time Purchase'; });
        all('[data-mpdp-ship-pill]').forEach(function (el) { el.style.display = isTen ? 'none' : ''; });
        all('[data-mpdp-cardmeta]').forEach(function (el) { el.textContent = (pack.count || 0) + ' bars · delivered monthly'; });
        all('[data-mpdp-size-badge]').forEach(function (el) { el.textContent = state.size === '10' ? 'Starter Pack' : 'Most Popular'; });

        // Flavors without a subscription show a standalone one-time card instead of the subscribe card.
        all('[data-mpdp-when-sub]').forEach(function (el) { el.style.display = canSub ? '' : 'none'; });
        all('[data-mpdp-when-nosub]').forEach(function (el) { el.style.display = canSub ? 'none' : ''; });

        var otPay = num(pack.onetime);
        var otWas = num(pack.compare) || otPay;
        var otPct = otWas > otPay ? Math.round((1 - otPay / otWas) * 100) : 0;
        all('[data-mpdp-ot-total]').forEach(function (el) { el.textContent = money(otPay); });
        all('[data-mpdp-ot-compare]').forEach(function (el) {
          el.textContent = money(otWas);
          var wrap = el.parentNode; if (wrap) wrap.style.display = otPct > 0 ? '' : 'none';
        });
        all('[data-mpdp-ot-saving]').forEach(function (el) {
          el.textContent = money(otWas - otPay);
          var row = el.closest ? el.closest('[data-mpdp-ot-saving-row]') : null;
          if (row) row.style.display = otPct > 0 ? '' : 'none';
        });
        all('[data-mpdp-ot-perbar]').forEach(function (el) { el.textContent = perbar(otPay / (pack.count || 1)); });
        all('[data-mpdp-ot-meta]').forEach(function (el) { el.textContent = (pack.count || 0) + ' bars · one-time'; });
        all('[data-mpdp-ot-badge]').forEach(function (el) {
          el.textContent = 'Save ' + otPct + '%';
          el.style.display = otPct > 0 ? '' : 'none';
        });
        // bare number, for copy like "be the first · 30% off" (discount differs per pack)
        all('[data-mpdp-ot-pct]').forEach(function (el) { el.textContent = otPct; });
        // Subscribe discount % vs one-time — plan-independent, so the subscribe-card "Save X%"
        // badge is correct per pack (e.g. 15% on the 10-pack, 30% on the 30-pack).
        var subPct = num(pack.onetime) > 0 ? Math.round((1 - num(pack.subfirst) / num(pack.onetime)) * 100) : 0;
        all('[data-mpdp-subpct]').forEach(function (el) { el.textContent = subPct > 0 ? subPct : 0; });

        // Subscription-only model: the 10-pack subscription equals its reference price (no
        // on-site discount), so hide the strike + "You're saving" row when there's nothing to
        // save. Multi-box packs (30) are framed as Buy 2, Get 1 Free.
        var hasSaving = subPct > 0 || hasCode;
        all('[data-mpdp-sub-strike]').forEach(function (el) { el.style.display = hasSaving ? '' : 'none'; });
        all('[data-mpdp-saving-row]').forEach(function (el) { el.style.display = hasSaving ? 'flex' : 'none'; });
        // B2G1 layout: the "& Save $X" suffix on the "Autoship" header hides on the no-discount 10-pack.
        all('[data-mpdp-save-suffix]').forEach(function (el) { el.style.display = hasSaving ? '' : 'none'; });
        all('[data-mpdp-sub-badge]').forEach(function (el) {
          var isBogo = (pack.count || 0) >= 30;
          el.textContent = isBogo ? 'Buy 2, Get 1 Free' : '';
          el.style.display = isBogo ? '' : 'none';
        });

        // Auto-applied discount code (e.g. Mandi's 10%): show the price the shopper actually
        // pays, struck against the pack's full price, with the TOTAL saving — not just the
        // code's share. cfg.codePct defaults to 0, so other PDPs are unaffected.
        var fullPrice = num(pack.full) || num(pack.onetime);
        var payNow = payMain;
        var savedAmt = fullPrice - payNow;
        var savedPct = fullPrice > 0 ? Math.round((1 - payNow / fullPrice) * 100) : 0;
        all('[data-mpdp-full]').forEach(function (el) { el.textContent = money(fullPrice); });
        all('[data-mpdp-final]').forEach(function (el) { el.textContent = money(payNow); });
        all('[data-mpdp-savedamt]').forEach(function (el) { el.textContent = money(savedAmt > 0 ? savedAmt : 0); });
        all('[data-mpdp-savedpct]').forEach(function (el) { el.textContent = savedPct > 0 ? savedPct : 0; });
        if (hasCode) {
          all('[data-mpdp-plan]').forEach(function (row) {
            var key = row.getAttribute('data-mpdp-plan');
            var pr = row.querySelector('[data-mpdp-plan-price]');
            if (pr) pr.textContent = money(applyCode(num(key === 'subscribe' ? pack.subfirst : pack.onetime)));
          });
        }

        // Cheapest entry price for this flavor — subscribe first-order where there's a plan,
        // otherwise one-time. Drives the closing CTA ("...for only $27.99").
        var lowest = 0;
        Object.keys(PACKS).forEach(function (k) {
          var p = PACKS[k] || {};
          var entry = (canSub && p.subfirst) ? num(p.subfirst) : num(p.onetime);
          if (entry > 0 && (lowest === 0 || entry < lowest)) lowest = entry;
        });
        if (lowest > 0) all('[data-mpdp-lowest]').forEach(function (el) { el.textContent = money(lowest); });
      }

      function addWith(mode) {
        var pk = packsFor()[state.size]; if (!pk) { showToast('Unavailable'); return; }
        // Variety pack: the variant + plan depend on the chosen free-box flavor.
        var variantId = (pk.variants && pk.variants[state.choice]) ? pk.variants[state.choice] : pk.variant;
        var planId = (pk.plans && pk.plans[state.choice]) ? pk.plans[state.choice] : pk.plan;
        // Subscribe: use the chosen delivery frequency's plan (from the Stay group).
        if (mode === 'subscribe' && state.freq) planId = state.freq;
        if (!variantId) { showToast('Unavailable'); return; }
        var item = { id: variantId, quantity: 1 };
        if (mode === 'subscribe' && planId) item.selling_plan = Number(planId);
        cartAdd(item, mode === 'subscribe' ? 'Subscription added to cart' : 'Added to cart');
      }

      all('[data-mpdp-size]').forEach(function (b) { b.addEventListener('click', function () { state.size = b.getAttribute('data-mpdp-size'); render(); }); });
      all('[data-mpdp-plan]').forEach(function (b) { b.addEventListener('click', function () { state.plan = b.getAttribute('data-mpdp-plan'); render(); }); });
      all('[data-mpdp-freq]').forEach(function (b) { b.addEventListener('click', function (e) { e.stopPropagation(); }); b.addEventListener('change', function () { state.freq = b.value; render(); }); });
      all('[data-mpdp-cta-plan]').forEach(function (b) { b.addEventListener('click', function () { state.plan = b.getAttribute('data-mpdp-cta-plan'); render(); scrollToCheckout(); }); });
      all('[data-mpdp-add]').forEach(function (b) { b.addEventListener('click', function () { addWith(state.plan); }); });
      all('[data-mpdp-add-sub]').forEach(function (b) { b.addEventListener('click', function () { addWith('subscribe'); }); });
      all('[data-mpdp-add-onetime]').forEach(function (b) { b.addEventListener('click', function () { addWith('onetime'); }); });

      // Flavor toggle: swap the product/prices AND recolor the whole page (adds body.pbcc-theme).
      function selectFlavor(f) {
        if (FLAVORS && !FLAVORS[f]) return;
        state.flavor = f;
        // adopt the new flavor's preferred pack + subscription capability
        var fc = FLAVORS ? (FLAVORS[f] || {}) : cfg;
        var fpacks = fc.packs || {};
        if (fc.defaultSize && fpacks[fc.defaultSize]) state.size = fc.defaultSize;
        else if (!fpacks[state.size]) state.size = Object.keys(fpacks)[0] || state.size;
        // Keep One-Time by default; only force it when the flavor has no subscription.
        if (fc.subscribe === false) state.plan = 'onetime';
        all('[data-mpdp-flavor]').forEach(function (x) { x.classList.toggle('is-active', x.getAttribute('data-mpdp-flavor') === f); });
        if (document.body) { document.body.classList.toggle('pbcc-theme', f === 'pbcc'); document.body.classList.toggle('variety-theme', f === 'variety'); }
        // swap flavor-specific galleries + any text tagged with data-fudge / data-pbcc
        all('[data-flavor-media]').forEach(function (el) { el.style.display = (el.getAttribute('data-flavor-media') === f) ? '' : 'none'; });
        all('[data-flavor-swap]').forEach(function (el) { var v = el.getAttribute('data-' + f); if (v !== null) el.textContent = v; });
        render();
      }
      all('[data-mpdp-flavor]').forEach(function (b) { b.addEventListener('click', function () { selectFlavor(b.getAttribute('data-mpdp-flavor')); }); });
      all('[data-mpdp-choice]').forEach(function (b) { b.addEventListener('click', function () { state.choice = b.getAttribute('data-mpdp-choice'); render(); }); });

      // Apply the starting flavor (honors ?flavor=) so theme, gallery, copy and prices all match on load.
      if (FLAVORS) selectFlavor(state.flavor); else render();
    }

    // ─────────── Presale / preorder box (no subscription) ───────────
    function initPreorder() {
      var cfgEl = document.querySelector('[data-mpdp-preorder-config]');
      if (!cfgEl) return;
      var cfg;
      try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
      var PACKS = cfg.packs || {};
      var keys = Object.keys(PACKS);
      if (!keys.length) return;
      var start = (cfg.defaultPack && PACKS[cfg.defaultPack]) ? cfg.defaultPack : (keys.indexOf('30') > -1 ? '30' : keys[0]);
      var state = { pack: start };

      function render() {
        var c = PACKS[state.pack] || PACKS[keys[0]]; if (!c) return;
        var pay = num(c.presale);
        var list = num(c.list) || pay;
        var save = list - pay;
        var hasSave = save > 0.005;
        all('[data-mpdp-po-total]').forEach(function (el) { el.textContent = money(pay); });
        all('[data-mpdp-po-list]').forEach(function (el) { el.textContent = money(list); });
        all('[data-mpdp-po-list-wrap]').forEach(function (el) { el.style.display = hasSave ? '' : 'none'; });
        all('[data-mpdp-po-save]').forEach(function (el) { el.textContent = money(hasSave ? save : 0); });
        all('[data-mpdp-po-save-suffix]').forEach(function (el) { el.style.display = hasSave ? '' : 'none'; });
        all('[data-mpdp-po-perbar]').forEach(function (el) { el.textContent = perbar(pay / (c.count || 1)); });
        all('[data-mpdp-po-cta]').forEach(function (el) { el.textContent = 'Add to Cart'; });
        all('[data-mpdp-pack]').forEach(function (card) {
          card.classList.toggle('is-active', card.getAttribute('data-mpdp-pack') === state.pack);
        });
      }

      all('[data-mpdp-pack]').forEach(function (b) { b.addEventListener('click', function () { state.pack = b.getAttribute('data-mpdp-pack'); render(); }); });
      all('[data-mpdp-reserve]').forEach(function (b) { b.addEventListener('click', function () {
        var c = PACKS[state.pack]; if (!c || !c.variant) { showToast('Unavailable'); return; }
        cartAdd({ id: c.variant, quantity: 1 }, (c.label || 'Pack') + ' added');
      }); });
      render();
    }

    // ─────────── Countdown timer ───────────
    function initCountdown() {
      var els = all('[data-mpdp-countdown]');
      if (!els.length) return;
      var pad = function (n) { return String(n).padStart(2, '0'); };
      function set(el, sel, v) { var t = el.querySelector(sel); if (t) t.textContent = v; }
      function tick() {
        els.forEach(function (el) {
          var target = new Date(el.getAttribute('data-target')).getTime();
          if (isNaN(target)) return;
          var diff = Math.max(0, target - Date.now());
          var d = Math.floor(diff / 86400000); diff -= d * 86400000;
          var h = Math.floor(diff / 3600000); diff -= h * 3600000;
          var m = Math.floor(diff / 60000); diff -= m * 60000;
          var s = Math.floor(diff / 1000);
          set(el, '[data-mpdp-cd-days]', String(d));
          set(el, '[data-mpdp-cd-hours]', pad(h));
          set(el, '[data-mpdp-cd-mins]', pad(m));
          set(el, '[data-mpdp-cd-secs]', pad(s));
        });
      }
      tick();
      setInterval(tick, 1000);
    }

    // ─────────── Generic wiring (always) ───────────
    // Sticky ATC reveal: show only once the (now higher-in-the-fold) buy box has
    // scrolled out of view. Gated via data-sticky-gated so no-JS = always visible.
    (function () {
      var stickies = all('[data-mpdp-sticky]');
      var boxes = all('[data-mpdp-checkout]');
      if (!stickies.length || !boxes.length || !('IntersectionObserver' in window)) return;
      stickies.forEach(function (s) { s.setAttribute('data-sticky-gated', ''); });
      var vis = [];
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { vis[boxes.indexOf(e.target)] = e.isIntersecting; });
        var anyVisible = boxes.some(function (b, i) { return vis[i]; });
        stickies.forEach(function (s) { s.classList.toggle('is-visible', !anyVisible); });
      });
      boxes.forEach(function (b) { io.observe(b); });
    })();

    all('[data-mpdp-scroll-checkout]').forEach(function (b) { b.addEventListener('click', scrollToCheckout); });
    all('[data-mpdp-open-cart]').forEach(function (b) { b.addEventListener('click', function () {
      var d = document.querySelector('cart-drawer');
      if (d && d.open) d.open(); else window.location.href = '/cart';
    }); });

    // Desktop hero gallery: click thumbnails / arrows to swap the main image.
    all('[data-mpdp-gallery]').forEach(function (g) {
      var slides = Array.prototype.slice.call(g.querySelectorAll('[data-mpdp-gallery-slide]'));
      var thumbs = Array.prototype.slice.call(g.querySelectorAll('[data-mpdp-gallery-thumb]'));
      if (slides.length < 2) return;
      var idx = 0;
      function show(i) {
        idx = (i + slides.length) % slides.length;
        slides.forEach(function (s, n) { s.style.display = n === idx ? 'block' : 'none'; });
        thumbs.forEach(function (t, n) { t.classList.toggle('is-active', n === idx); });
      }
      thumbs.forEach(function (t, n) { t.addEventListener('click', function () { show(n); }); });
      var prev = g.querySelector('[data-mpdp-gallery-prev]'); if (prev) prev.addEventListener('click', function () { show(idx - 1); });
      var next = g.querySelector('[data-mpdp-gallery-next]'); if (next) next.addEventListener('click', function () { show(idx + 1); });
      show(0);
    });

    // Peek gallery (soar PDP hero): thumbnails scroll the strip; highlight the in-view slide.
    all('[data-soar-gallery]').forEach(function (g) {
      var strip = g.querySelector('[data-soar-peek]');
      if (!strip) return;
      var slides = Array.prototype.slice.call(g.querySelectorAll('[data-soar-slide]'));
      var thumbs = Array.prototype.slice.call(g.querySelectorAll('[data-soar-thumb]'));
      var progress = (g.parentElement || g).querySelector('[data-soar-progress-thumb]');
      if (slides.length < 2) return;
      function setActive(i) { thumbs.forEach(function (t, n) { t.classList.toggle('is-active', n === i); }); }
      function setProgress() {
        if (!progress) return;
        var max = strip.scrollWidth - strip.clientWidth;
        var vis = Math.max(18, (strip.clientWidth / strip.scrollWidth) * 100);
        var pos = max > 0 ? (strip.scrollLeft / max) * (100 - vis) : 0;
        progress.style.width = vis + '%';
        progress.style.left = pos + '%';
      }
      setProgress();
      thumbs.forEach(function (t, n) { t.addEventListener('click', function () {
        strip.scrollTo({ left: slides[n].offsetLeft, behavior: 'smooth' });
        setActive(n);
      }); });
      // Desktop prev/next arrows: scroll to the adjacent VISIBLE slide (skips the hidden macro slide).
      function currentIndex() {
        var c = strip.scrollLeft + strip.clientWidth / 2, best = 0, bd = Infinity;
        slides.forEach(function (s, n) { if (s.offsetWidth === 0) return; var m = s.offsetLeft + s.offsetWidth / 2, d = Math.abs(m - c); if (d < bd) { bd = d; best = n; } });
        return best;
      }
      function goTo(dir) {
        var n = currentIndex() + dir;
        while (n >= 0 && n < slides.length && slides[n].offsetWidth === 0) n += dir;
        if (n < 0 || n >= slides.length) return;
        strip.scrollTo({ left: slides[n].offsetLeft, behavior: 'smooth' });
      }
      var gprev = g.querySelector('[data-soar-gallery-prev]'); if (gprev) gprev.addEventListener('click', function () { goTo(-1); });
      var gnext = g.querySelector('[data-soar-gallery-next]'); if (gnext) gnext.addEventListener('click', function () { goTo(1); });
      var raf;
      strip.addEventListener('scroll', function () {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          var c = strip.scrollLeft + strip.clientWidth / 2, best = 0, bd = Infinity;
          slides.forEach(function (s, n) { var m = s.offsetLeft + s.offsetWidth / 2, d = Math.abs(m - c); if (d < bd) { bd = d; best = n; } });
          setActive(best);
          setProgress();
        });
      }, { passive: true });
    });

    // Arrow carousels (e.g. the science cards): prev/next scroll by one card,
    // clamped to the ends so you can't over-scroll into empty space, and the
    // arrows disable at each end.
    all('[data-soar-carousel]').forEach(function (c) {
      var track = c.querySelector('[data-soar-track]');
      if (!track) return;
      var p = c.querySelector('[data-soar-prev]');
      var n = c.querySelector('[data-soar-next]');
      function maxScroll() { return Math.max(0, track.scrollWidth - track.clientWidth); }
      function sync() {
        var x = track.scrollLeft, max = maxScroll();
        if (p) { p.disabled = x <= 1; p.style.opacity = p.disabled ? '0.35' : ''; p.style.cursor = p.disabled ? 'default' : ''; }
        if (n) { n.disabled = x >= max - 1; n.style.opacity = n.disabled ? '0.35' : ''; n.style.cursor = n.disabled ? 'default' : ''; }
      }
      function step(dir) {
        var card = track.children[0];
        var amt = card ? card.offsetWidth + 14 : track.clientWidth * 0.8;
        var target = Math.max(0, Math.min(maxScroll(), track.scrollLeft + dir * amt));
        track.scrollTo({ left: target, behavior: 'smooth' });
      }
      if (p) p.addEventListener('click', function () { step(-1); });
      if (n) n.addEventListener('click', function () { step(1); });
      track.addEventListener('scroll', function () { window.requestAnimationFrame(sync); }, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });

    var subCfg = document.querySelector('[data-mpdp-config]');
    if (subCfg) initSubscription(subCfg);
    initPreorder();
    initCountdown();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
